import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, FileDown, Loader2 } from 'lucide-react';
import { exportSummaryReportToPDF } from '../../utils/pdfExport';
import { apiClient } from '../../utils/apiHelper';
import Pagination from '@/components/Pagination';
import { formatDate } from '../../utils/dateTimeUtils';

const MONTHS = [
  { value: '', label: 'All Months' },
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' }
];

const FeesReport = () => {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({ total: 0, totalCollected: 0, totalPending: 0 });
  const [month, setMonth] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [collegeInfo, setCollegeInfo] = useState(null);

  useEffect(() => {
    fetchReport();
    fetchCollegeInfo();
  }, [currentPage, itemsPerPage]);

  const fetchCollegeInfo = async () => {
    try {
      const response = await apiClient.get('/api/tenants/my-college/settings');
      setCollegeInfo(response.data);
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const getDateRange = () => {
    if (!month || !year) return {};
    const m = parseInt(month);
    const y = parseInt(year);
    const from_date = `${y}-${String(m).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const to_date = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { from_date, to_date };
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);

      const { from_date, to_date } = getDateRange();
      if (from_date) params.append('from_date', from_date);
      if (to_date) params.append('to_date', to_date);

      const response = await apiClient.get(`/api/fees/payments?${params.toString()}`);

      // apiClient interceptor unwraps {data: [...]} → response.data = [...], response.pagination = {...}
      let paymentsData = [];
      let total = 0;
      let totalCollected = 0;

      if (Array.isArray(response.data)) {
        paymentsData = response.data;
        const pagination = response.pagination || {};
        total = pagination.total || response.data.length;
        totalCollected = pagination.stats?.total_amount || response.data.reduce((sum, p) => sum + (p.amount || p.amount_paid || 0), 0);
      } else {
        paymentsData = response.data?.data || [];
        total = response.data?.total || 0;
        totalCollected = response.data?.stats?.total_amount || 0;
      }

      setPayments(paymentsData);
      setTotalItems(total);
      setSummary({ total, totalCollected, totalPending: 0 });
    } catch (error) {
      console.error('Error:', error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = () => {
    setCurrentPage(1);
    fetchReport();
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleItemsPerPageChange = (val) => { setItemsPerPage(val); setCurrentPage(1); };

  const exportToPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('page', 1);
      params.append('limit', 5000);

      const { from_date, to_date } = getDateRange();
      if (from_date) params.append('from_date', from_date);
      if (to_date) params.append('to_date', to_date);

      const response = await apiClient.get(`/api/fees/payments?${params.toString()}`);
      const allPayments = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const paginationStats = response.pagination?.stats || {};
      const totalAmount = paginationStats.total_amount || allPayments.reduce((sum, p) => sum + (p.amount || p.amount_paid || 0), 0);

      if (allPayments.length === 0) { alert('No data to export'); return; }

      const monthLabel = month ? MONTHS.find(m => m.value === month)?.label : '';
      const pdfStats = {
        'Total Payments': allPayments.length,
        'Total Collected': `Rs. ${totalAmount.toFixed(2)}`,
        'Report Period': monthLabel && year ? `${monthLabel} ${year}` : 'All Time'
      };

      const columns = [
        { header: 'Receipt #', dataKey: 'receipt_number' },
        { header: 'Student', dataKey: 'student_name' },
        { header: 'Course', dataKey: 'course' },
        { header: 'Amount', dataKey: 'amount' },
        { header: 'Payment Date', dataKey: 'payment_date' },
        { header: 'Mode', dataKey: 'payment_mode' }
      ];

      const data = allPayments.map(p => ({
        receipt_number: p.receipt_number || 'N/A',
        student_name: p.student_name || 'N/A',
        course: p.course || p.course_name || 'N/A',
        amount: `Rs. ${(p.amount || p.amount_paid || 0).toFixed(2)}`,
        payment_date: p.payment_date ? formatDate(p.payment_date) : 'N/A',
        payment_mode: p.payment_mode || 'N/A'
      }));

      exportSummaryReportToPDF('Fees Collection Report', pdfStats, columns, data, 'fees_report.pdf', collegeInfo);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  return (
    <div className="p-6 space-y-6" data-testid="fees-report-page">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
          <CardTitle>Fees Report</CardTitle>
          <div className="flex gap-3 items-end flex-wrap">
            <div>
              <Label className="text-xs">Month</Label>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="flex h-10 w-40 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="fees-month-dropdown"
              >
                {MONTHS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Year</Label>
              <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="w-28" data-testid="fees-year-input" />
            </div>
            <Button onClick={handleGenerate} disabled={loading} data-testid="fees-generate-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              {loading ? 'Loading...' : 'Generate'}
            </Button>
            <Button onClick={exportToPDF} className="bg-green-600 hover:bg-green-700" disabled={totalItems === 0 || exporting} data-testid="fees-export-pdf-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Payments</p>
              <p className="text-2xl font-bold text-blue-600" data-testid="fees-total-count">{summary.total}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Collected</p>
              <p className="text-2xl font-bold text-green-600" data-testid="fees-total-collected">₹{(summary.totalCollected || 0).toFixed(2)}</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">₹{(summary.totalPending || 0).toFixed(2)}</p>
            </div>
          </div>

          {/* Fees Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="fees-report-table">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 text-sm font-semibold">SL</th>
                  <th className="text-left p-3 text-sm font-semibold">Receipt #</th>
                  <th className="text-left p-3 text-sm font-semibold">Student Name</th>
                  <th className="text-left p-3 text-sm font-semibold">Course</th>
                  <th className="text-left p-3 text-sm font-semibold">Fee Type</th>
                  <th className="text-left p-3 text-sm font-semibold">Amount</th>
                  <th className="text-left p-3 text-sm font-semibold">Payment Date</th>
                  <th className="text-left p-3 text-sm font-semibold">Mode</th>
                  <th className="text-left p-3 text-sm font-semibold">Entry User</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="9" className="text-center p-8 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Loading...</td></tr>
                ) : payments.length === 0 ? (
                  <tr><td colSpan="9" className="text-center p-8 text-gray-500">No fee payments found</td></tr>
                ) : (
                  payments.map((payment, idx) => (
                    <tr key={payment.id || idx} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-sm">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td className="p-3 text-sm font-medium">{payment.receipt_number || '-'}</td>
                      <td className="p-3 text-sm">{payment.student_name || '-'}</td>
                      <td className="p-3 text-sm">{payment.course || payment.course_name || payment.class_name || '-'}</td>
                      <td className="p-3 text-sm">{payment.payment_for || payment.fee_type || '-'}</td>
                      <td className="p-3 text-sm font-semibold text-green-600">₹{(payment.amount || payment.amount_paid || 0).toFixed(2)}</td>
                      <td className="p-3 text-sm">{payment.payment_date ? formatDate(payment.payment_date) : '-'}</td>
                      <td className="p-3 text-sm">{payment.payment_mode || '-'}</td>
                      <td className="p-3 text-sm text-gray-600">{payment.created_by_name || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default FeesReport;
